<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="container" @click.self="!loading">
    <div v-loading.center="{ isLoading: !!loading }" class="wrapper" :class="{ 'full-width': modal.fullWidth }">
      <div v-if="modal" class="text-center">
        <span class="stacked-icons">
          <ImBubbles2Icon class="chat-bubble-icon" height="10em" width="10em" />
          <component :is="modal.icon" class="chat-bubble-icon-secondary stacked-icon" height="5em" width="5em" />
        </span>
        <h3 class="mb-2 title">
          {{ modal.title }}
        </h3>

        <p v-if="modal.message" class="mb-2 subtitle">
          <template v-if="modal.message.truncate">
            "<TruncatedText :maxlength="100" :text="modal.message.text" />"
          </template>

          <span v-else v-html="modal.message.text" />
        </p>
      </div>

      <div class="button-container">
        <ThemeButton
          v-for="btn in modal.buttons"
          :key="btn.id"
          class="block-button"
          :outlined="btn.type === 'outlined'"
          :text="btn.type === 'text'"
          @click="btn.click"
        >
          {{ btn.text }}
        </ThemeButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-property-decorator';
import * as PleasedApi from '@/common/api/api.pleased';

import loading from '@/components/loading';
import { ActiveOpenChat, VerifyChatOpenDto } from '@/common/api/models.pleased';
import ThemeButton from './ThemeButton.vue';
import ImBubbles2Icon from './icons/ImBubbles2Icon.vue';
import TruncatedText from './TruncatedText.vue';
import * as Locale from '@/plugins/i18n';
import MdReplay from './icons/MdReplay.vue';
import MdQuestionMark from './icons/MdQuestionMark.vue';
import HiDotsHorizontal from './icons/HiDotsHorizontal.vue';
import { TranslateResult } from 'vue-i18n';
import { GA_EVENT_CUSTOM_DIMENSION_ENUM, GA_EVENT_NAME_ENUM } from '@/common/gaHelper';
import { BTN_EVTS } from '@/directives/trackClick';
import LogPageEventMixin, { PAGE_EVENTS, PAGE_TRACKING } from '@/mixins/logPageEvent';
import { analytics } from '@/common/logs/EventLogger';

interface ConfirmResumableChatProps {
  /**
   * Props
   */
  resumableChat: ActiveOpenChat;
  /**
   * Close the chat.
   */
  onCancel?: (resumableChat: ActiveOpenChat) => void;
  onNewChat?: (resumableChat: ActiveOpenChat) => void;
  onActiveConversation?: (resumableChat: ActiveOpenChat) => void;
  onMyRequests?: (resumableChat: ActiveOpenChat) => void;
  /**
   * Resume the chat.
   */
  onResume?: (chat: ActiveOpenChat, canBeResumed: VerifyChatOpenDto) => void;
}

enum Route {
  ConfirmResumableChat = 'ConfirmResumableChat',
  ClosedActiveConversationChat = 'ClosedActiveConversationChat',
  ClosedMyRequestChat = 'ClosedMyRequestChat',
}

const btn = (
  type: 'outlined' | 'primary' | 'text',
  text: string | TranslateResult,
  click: (e: MouseEvent) => void
) => ({ id: `${type}-${text}`, text, type, click });

const RESUMABLE_CHAT_EVENTS = {
  resume: 'resume',
  cancel: 'cancel',
  activeConversation: 'activeConversation',
  newChat: 'newChat',
  myRequests: 'myRequests',
} as const;

type ResumableChatEvent = (typeof RESUMABLE_CHAT_EVENTS)[keyof typeof RESUMABLE_CHAT_EVENTS];

const EVENTS_TO_GA_MAP = {
  [RESUMABLE_CHAT_EVENTS.resume]: GA_EVENT_NAME_ENUM.RESUMABLE_CHAT_CONTINUE,
  [RESUMABLE_CHAT_EVENTS.cancel]: GA_EVENT_NAME_ENUM.RESUMABLE_CHAT_CANCEL,
  [RESUMABLE_CHAT_EVENTS.activeConversation]: GA_EVENT_NAME_ENUM.RESUMABLE_CHAT_GO_TO_ACTIVE_CONVERSATION,
  [RESUMABLE_CHAT_EVENTS.newChat]: GA_EVENT_NAME_ENUM.RESUMABLE_CHAT_GO_TO_NEW_CHAT,
  [RESUMABLE_CHAT_EVENTS.myRequests]: GA_EVENT_NAME_ENUM.RESUMABLE_CHAT_GO_TO_MY_REQUESTS,
} as const;

const MODAL_ROUTE_PAGE_EVENT_MAP = {
  [Route.ConfirmResumableChat]: PAGE_EVENTS.MODAL_CONFIRM_RESUMABLE_CHAT,
  [Route.ClosedActiveConversationChat]: PAGE_EVENTS.MODAL_CLOSED_AC_CHAT,
  [Route.ClosedMyRequestChat]: PAGE_EVENTS.MODAL_CLOSED_MY_REQUEST_CHAT,
} as const;

const MODAL_EVENTS_TO_CLICK_TRACK_EVENTS_MAP = {
  [Route.ConfirmResumableChat]: {
    [RESUMABLE_CHAT_EVENTS.resume]: BTN_EVTS.MODAL_CONFIRM_RESUMABLE_CHAT_CONTINUE,
    [RESUMABLE_CHAT_EVENTS.cancel]: BTN_EVTS.MODAL_CONFIRM_RESUMABLE_CHAT_END_CHAT,
  },
  [Route.ClosedActiveConversationChat]: {
    [RESUMABLE_CHAT_EVENTS.activeConversation]: BTN_EVTS.MODAL_CLOSED_AC_CHAT_GO_TO_AC,
    [RESUMABLE_CHAT_EVENTS.newChat]: BTN_EVTS.MODAL_CLOSED_AC_CHAT_NEW_CONVERSATION,
  },
  [Route.ClosedMyRequestChat]: {
    [RESUMABLE_CHAT_EVENTS.myRequests]: BTN_EVTS.MODAL_CLOSED_MY_REQUEST_CHAT_GO_TO_MY_REQUESTS,
    [RESUMABLE_CHAT_EVENTS.newChat]: BTN_EVTS.MODAL_CLOSED_MY_REQUEST_CHAT_NEW_CONVERSATION,
  },
} as const;

/**
 * Dialog component to allow the end user to resume a chat with the support team.
 * NOTE: This component is used as a JSX component in the pleased application, and
 * the global i18n service is not available here, hence, we use the locale service directly.
 */
@Component({
  directives: { loading },
  components: { ImBubbles2Icon, ThemeButton, TruncatedText },
  mixins: [LogPageEventMixin],
})
export default class ConfirmResumableChat extends Vue {
  @Prop() readonly resumableChat!: ActiveOpenChat;
  loading = false;
  route: Route = Route.ConfirmResumableChat;
  resolvedTicketId: null | number = null;

  get [PAGE_TRACKING]() {
    return {
      name: MODAL_ROUTE_PAGE_EVENT_MAP[this.route],
      selection: this.resumableChat.id,
    };
  }

  get description() {
    const subject = this.resumableChat?.subject?.trim();
    return subject;
  }

  get text() {
    return {
      endChat: Locale.i18n.t('global.end_chat'),
    };
  }

  get modal() {
    const createEventHandler = (event: ResumableChatEvent) => () => {
      const gaEvent = EVENTS_TO_GA_MAP[event];
      const modalEventsMap = MODAL_EVENTS_TO_CLICK_TRACK_EVENTS_MAP[this.route] as Record<ResumableChatEvent, string>;

      if (gaEvent) {
        this.$gtag.event(gaEvent);
      }

      if (modalEventsMap && modalEventsMap[event]) {
        analytics.trackClick({
          name: modalEventsMap[event],
          selection: this.resumableChat.id,
        });
      }

      this.$emit(
        event,
        !this.resumableChat.ticketId ? { ...this.resumableChat, ticketId: this.resolvedTicketId } : this.resumableChat
      );
    };

    const RouteModal = {
      [Route.ConfirmResumableChat]: () => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(this.description ?? '', 'text/html');

        return {
          title: Locale.i18n.t('page.resumable_chat.confirm.title'),
          icon: HiDotsHorizontal,
          message: { text: doc.documentElement.textContent, truncate: true },
          buttons: [
            btn('text', Locale.i18n.t('page.resumable_chat.confirm.btn.continue'), this.checkIfChatIsResumable),
            btn('primary', Locale.i18n.t('global.end_chat'), createEventHandler('cancel')),
          ],
        };
      },
      [Route.ClosedActiveConversationChat]: () => ({
        title: Locale.i18n.t('page.resumable_chat.cannot_resume.title'),
        icon: MdReplay,
        message: {
          text: `${Locale.i18n.t(
            'page.resumable_chat.cannot_resume.active_conversation_message_1'
          )}<br><br>${Locale.i18n.t('page.resumable_chat.cannot_resume.active_conversation_message_2')}`,
          html: true,
        },
        buttons: [
          btn(
            'primary',
            Locale.i18n.t('page.resumable_chat.confirm.btn.go_to_ac'),
            createEventHandler('activeConversation')
          ),
          btn('outlined', Locale.i18n.t('page.active_conversation.new'), createEventHandler('newChat')),
        ],
        fullWidth: true,
      }),
      [Route.ClosedMyRequestChat]: () => ({
        title: Locale.i18n.t('page.resumable_chat.cannot_resume.title'),
        icon: MdQuestionMark,
        message: {
          text: `${Locale.i18n.t('page.resumable_chat.cannot_resume.my_requests_message_1')}<br><br>${Locale.i18n.t(
            'page.resumable_chat.cannot_resume.my_requests_message_2'
          )}`,
          html: true,
        },
        buttons: [
          btn(
            'primary',
            Locale.i18n.t('page.resumable_chat.confirm.btn.go_to_my_request'),
            createEventHandler('myRequests')
          ),
          btn('outlined', Locale.i18n.t('page.active_conversation.new'), createEventHandler('newChat')),
        ],
        fullWidth: true,
      }),
    };

    return RouteModal[this.route]?.();
  }

  async checkIfChatIsResumable() {
    if (this.loading) return;
    this.loading = true;
    const canBeResumed = await PleasedApi.checkChatCanBeResumed({ chatId: this.resumableChat.id })
      .catch(() => ({ canResume: false, inActiveConversation: false, ticketId: null }))
      .finally(() => (this.loading = false));
    if (canBeResumed.canResume) {
      this.$gtag.event(GA_EVENT_NAME_ENUM.RESUMABLE_CHAT_CONTINUE);
      analytics.trackClick({
        name: BTN_EVTS.MODAL_CONFIRM_RESUMABLE_CHAT_CONTINUE,
        selection: this.resumableChat.id,
      });
      return this.$emit('resume', this.resumableChat, canBeResumed);
    }

    this.resolvedTicketId = canBeResumed.ticketId || null;

    // Track page close + new enter event here since we switch to another modal route.
    analytics.trackPageEvent({
      name: PAGE_EVENTS.MODAL_CONFIRM_RESUMABLE_CHAT,
      enter: false,
      selection: this.resumableChat.id,
    });

    this.route = canBeResumed.inActiveConversation ? Route.ClosedActiveConversationChat : Route.ClosedMyRequestChat;

    analytics.trackPageEvent({
      name: MODAL_ROUTE_PAGE_EVENT_MAP[this.route],
      enter: true,
      selection: this.resumableChat.id,
    });
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/base.scss';
$border-radius: 5px;

.container {
  height: 100vh;
  width: 100vw;
  background: rgba(0, 0, 0, 0.23);
  display: flex;
  justify-content: center;
  align-items: center;
}

.wrapper {
  max-width: 75vw;
  font-size: 1.5rem;
  border-radius: $border-radius;
  position: relative;
  background: $continue-chat-background-color;
  padding: 3rem;
  color: $continue-chat-text-color;

  box-shadow:
    0 10px 20px rgba(0, 0, 0, 0.19),
    0 6px 6px rgba(0, 0, 0, 0.23);

  &.full-width {
    max-width: 100vw;
  }

  .stacked-icons {
    position: relative;
    display: inline-block;
    .stacked-icon {
      position: absolute;
      top: 1.5em;
      left: 1.5em;
    }
  }

  .block-button {
    padding: 1.1rem 0;
    margin-top: 10px;
    cursor: pointer;
  }

  .chat-bubble-icon {
    color: $continue-chat-icon-color;
  }
  .chat-bubble-icon-secondary {
    color: $continue-chat-background-color;
  }

  .button-container {
    display: flex;
    flex-direction: column;
  }

  .title {
    position: relative;
    padding: 1rem;
    z-index: 101;
    display: flex;
    align-items: center;
    font-weight: 500;
    border: {
      top-left-radius: $border-radius;
      top-right-radius: $border-radius;
    }
  }
}
</style>
